import users_api from "../../api/users"
import {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import GenericDialog from "../../components/GenericDialog";
import GenericInput from "../../components/GenericInput";
import TableDropdown from "../../components/TableDropdown";
import auth from "../../api/auth";



const Accreditation = () => {

    const navigate = useNavigate()
    const location = useLocation();

    // Table Content
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [page, setPage] = useState(0)
    const [totalPages, setTotalPages] = useState(1)
    const [pageData, setPageData] = useState([])
    const [sortOrder, setSortOrder] = useState("desc")
    const [name, setName] = useState("")
    const [searchQuery, setSearchQuery] = useState("");

    const [typeFilter, setTypeFilter] = useState(null)
    const [verifiedFilter, setVerifiedFilter] = useState(null)
    const [showTypeFilter, setShowTypeFilter] = useState(false)
    const [showVerifiedFilter, setShowVerifiedFilter] = useState(false)
    const [typeOptions, setTypeOptions] = useState([])
    const verifiedOptions = [
        {id: "verified", name: "Verified"},
        {id: "declined", name: "Declined"},
        {id: "unverified", name: "Unverified"}
    ]
    const [selectedFilters, setSelectedFilters] = useState([
        {id: 1, type: "type", value: ""},
        {id: 2, type: "verified", value: "Unverified"}
    ])

    // Error Dialog
    const [dialogTitle, setDialogTitle] = useState("")
    const [dialogText, setDialogText] = useState("")
    const buttons = [{"text":"OK", "id": "1"}];

    useEffect(() => {
        auth.hasSession().then((session) => {
            if (!session) window.location.assign("/logout");
        });
    }, [])

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const search = params.get("q") || "";
        const pageParam = parseInt(params.get("p"), 10);
        const ipp = parseInt(params.get("ipp"), 10);
        const t = params.get("t") || null;
        const v = params.get("v");
        const o = params.get("o");
        const order = o === "asc" ? "asc" : "desc";
        const defaultVerified = verifiedOptions.find((opt) => opt.id === "unverified");

        setName(search);
        setSearchQuery(search);
        if (!isNaN(pageParam)) setPage(pageParam);
        if (!isNaN(ipp)) setItemsPerPage(ipp);
        setSortOrder(order);
        if (t) setTypeFilter({id: t, name: t});
        if (v !== null) {
            const verifiedObj = verifiedOptions.find((o) => o.id === v);
            setVerifiedFilter(verifiedObj || null);
        } else {
            setVerifiedFilter(defaultVerified);
        }

        setSelectedFilters([
            {id: 1, type: "type", value: t || ""},
            {id: 2, type: "verified", value: v !== null ? (verifiedOptions.find((o) => o.id === v)?.name || "") : "Unverified"}
        ]);
    }, []);

    useEffect(() => {
        GetAccreditation()
    }, [page, itemsPerPage, searchQuery, typeFilter, verifiedFilter, sortOrder]);

    const updateUrlParams = (updates) => {
        const params = new URLSearchParams(location.search);

        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === "" || value === undefined) {
                params.delete(key);
            } else {
                params.set(key, value.toString());
            }
        });

        navigate({ search: params.toString() });
    };

    const applyFilters = (items) => {
        return items.filter((item) => {
            const matchesSearch = searchQuery
                ? (
                    item.user?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (item.certificate_number || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.id?.toString().includes(searchQuery)
                )
                : true;
            const matchesType = typeFilter?.id ? item.type === typeFilter.id : true;
            const matchesVerified = verifiedFilter !== null ? item.status === verifiedFilter.id : true;
            return matchesSearch && matchesType && matchesVerified;
        });
    }

    const sortById = (items) => {
        return [...items].sort((a, b) => sortOrder === "asc" ? a.id - b.id : b.id - a.id);
    }

    const GetAccreditation = async () => {
        try {
            const hasFilters = !!(searchQuery || typeFilter?.id || verifiedFilter !== null);
            if (!hasFilters) {
                const response = await users_api.GetAccreditations(itemsPerPage, page * itemsPerPage, sortOrder);
                const data = response.data.accreditations || [];
                setPageData(data);

                const uniqueTypes = [...new Set(data.map((item) => item.type).filter(Boolean))]
                    .map((type) => ({id: type, name: type}));
                setTypeOptions(uniqueTypes);

                let pagesNeeded = Math.ceil((response.data.total_count || 0) / itemsPerPage)
                if (!pagesNeeded || pagesNeeded < 1) pagesNeeded = 1
                setTotalPages(pagesNeeded)
                return;
            }

            const countResponse = await users_api.GetAccreditations(1, 0, sortOrder);
            const totalCount = countResponse?.data?.total_count || 0;
            const allResponse = await users_api.GetAccreditations(totalCount || 1, 0, sortOrder);
            const allItems = allResponse?.data?.accreditations || [];
            const filteredItems = sortById(applyFilters(allItems));

            const uniqueTypes = [...new Set(allItems.map((item) => item.type).filter(Boolean))]
                .map((type) => ({id: type, name: type}));
            setTypeOptions(uniqueTypes);

            const start = page * itemsPerPage;
            const end = start + itemsPerPage;
            setPageData(filteredItems.slice(start, end));

            let pagesNeeded = Math.ceil(filteredItems.length / itemsPerPage)
            if (!pagesNeeded || pagesNeeded < 1) pagesNeeded = 1
            setTotalPages(pagesNeeded)
        } catch (err) {
            setDialogTitle("Fetching Error")
            setDialogText("There was an error fetching the accreditations. Please try again")
        }
    }

    const handleNumberClick = (ev) => {
        if (!page && page !== 0) return;
        if (!ev.target.id && ev.target.id !== 0) return;
        let pg = parseInt(ev.target.id)
        setPage(pg);
        updateUrlParams({ p: pg });
    }

    const handleNextPageClick = () => {
        if (!page && page !== 0) return;
        if (page + 1 < totalPages) {
            const next = page + 1;
            setPage(next)
            updateUrlParams({ p: next });
        }
    }

    const handlePreviousPageClick = () => {
        if (!page && page !== 0) return;
        if (page > 0) {
            const prev = page - 1;
            setPage(prev)
            updateUrlParams({ p: prev });
        }
    }

    const handleErrorDialogResult = (res) => {
        if(!res) return;
        setDialogText("")
        setDialogTitle("")
    }

    const handleDropdown = (title) => {
        switch (title) {
            case "type":
                setShowTypeFilter(!showTypeFilter)
                break;
            case "verified":
                setShowVerifiedFilter(!showVerifiedFilter)
                break;
            default:
                break;
        }
    }

    const addSelectedFilters = (idx, value) => {
        let temp = [...selectedFilters]
        temp[idx].value = value
        setSelectedFilters(temp)
    }

    const addFilter = (obj, title) => {
        if (!obj || !title) return;
        switch (title) {
            case "type":
                setTypeFilter(obj)
                setShowTypeFilter(false)
                addSelectedFilters(0, obj.name)
                setPage(0)
                updateUrlParams({ t: obj.id, p: 0 });
                break;
            case "verified":
                setVerifiedFilter(obj)
                setShowVerifiedFilter(false)
                addSelectedFilters(1, obj.name)
                setPage(0)
                updateUrlParams({ v: obj.id, p: 0 });
                break;
            default:
                break;
        }
    }

    const removeFilter = (id) => {
        let temp = [...selectedFilters]
        temp[id - 1].value = ""
        setSelectedFilters(temp)
        switch (id) {
            case 1:
                setTypeFilter(null)
                setPage(0)
                updateUrlParams({ t: null, p: 0 });
                break;
            case 2:
                setVerifiedFilter(null)
                setPage(0)
                updateUrlParams({ v: null, p: 0 });
                break;
            default:
                break;
        }
    }

    const applySearch = () => {
        setSearchQuery(name);
        setPage(0);
        updateUrlParams({ q: name, p: 0 });
    };

    return (
        <div className="page-container">
            <div className="page-title">
                Accreditation
            </div>
            <div className="page-subtitle">
                All Accreditation
            </div>
            <div className="items-per-page-container">
                <label>Items per page: </label>
                <select
                    value={itemsPerPage}
                    onChange={(e) => {
                        setItemsPerPage(parseInt(e.target.value));
                        setPage(0);
                        updateUrlParams({ ipp: e.target.value, p: 0 });
                    }}
                >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
                <label style={{ marginLeft: "12px" }}>Order: </label>
                <select
                    value={sortOrder}
                    onChange={(e) => {
                        const nextOrder = e.target.value;
                        setSortOrder(nextOrder);
                        setPage(0);
                        updateUrlParams({ o: nextOrder, p: 0 });
                    }}
                >
                    <option value="desc">Desc</option>
                    <option value="asc">Asc</option>
                </select>
            </div>
            <div className="search-users-container">
                <div className="user-search-field-container">
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        applySearch();
                    }}>
                        <GenericInput label="Global search" value={name} updateValue={setName} />
                        <div className="search-actions-row">
                            <div className="search-button link" onClick={() => applySearch()}>
                                Search
                            </div>
                            <div className="reset-button link" onClick={() => {
                                setName("");
                                setSearchQuery("");
                                setPage(0);
                                updateUrlParams({ q: null, p: 0 });
                            }}>
                                Reset
                            </div>
                        </div>
                        <button type="submit" style={{display: "none"}}></button>
                    </form>
                </div>
            </div>
            <div className="user-search-filters-container">
                {selectedFilters && selectedFilters.map((filter) => {
                    return(
                        <div key={filter.id} className={filter.value !== "" ? "user-selected-filter-container" : "user-selected-filter-container hide"}>
                            <div className="user-selected-filter-cross-container link" onClick={() => removeFilter(filter.id)}>
                                x
                            </div>
                            <div className="user-selected-filter-text-container">{filter.value}</div>
                        </div>
                    )
                })}
            </div>
            <div className="table-container">
                <div className="table-row">
                    <div className="table-column heading">ID</div>
                    <div className="table-column heading">User</div>
                    <div className="table-column heading">
                        <div className="table-dropdown-button-container">
                            <div className="table-dropdown-button link" onClick={() => handleDropdown("type")}>{">"}</div>
                        </div>
                        {showTypeFilter ?
                            <TableDropdown options={typeOptions} dropdown={"type"} update={addFilter.bind(this)}/>
                            : null}
                        Type
                    </div>
                    <div className="table-column heading">Certificate Number</div>
                    <div className="table-column heading">
                        <div className="table-dropdown-button-container">
                            <div className="table-dropdown-button link" onClick={() => handleDropdown("verified")}>{">"}</div>
                        </div>
                        {showVerifiedFilter ?
                            <TableDropdown options={verifiedOptions} dropdown={"verified"} update={addFilter.bind(this)}/>
                            : null}
                        Accreditation Status
                    </div>
                    <div className="table-column heading last">More</div>
                </div>
                {pageData && pageData.map((item, idx) => {
                    return (
                        <div className="table-row">
                            <div className="table-column">{item.id}</div>
                            <div className="table-column">{item.user}</div>
                            <div className="table-column">{item.type}</div>
                            <div className="table-column">{item.certificate_number && item.certificate_number ? item.certificate_number : "N/A"}</div>
                            <div className="table-column">
                                {item.status === "verified" ? "Verified" : (item.status === "declined" ? "Declined" : "Unverified")}
                            </div>
                            <div className="table-column last">
                                <div className="table button link" onClick={() => navigate("/users/" + item.user)}>
                                    More Info
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className="table-navigation-button-container">
                <div className="table-left button link" onClick={() => handlePreviousPageClick()}>{"<"}</div>
                <div className="table-pages">
                    <div className={page === 0 ? "page active link" : "page link"}
                         onClick={handleNumberClick}
                         id="0"
                    >
                        1
                    </div>
                    {totalPages > 2 && (() => {
                        const middlePages = [];
                        const start = Math.max(1, page - 2);
                        const end = Math.min(totalPages - 2, page + 2);

                        if (start > 1) middlePages.push(<div key="dots-left" className="page dots">...</div>);

                        for (let i = start; i <= end; i++) {
                            middlePages.push(
                                <div key={i}
                                     className={page === i ? "page active link" : "page link"}
                                     onClick={handleNumberClick}
                                     id={i.toString()}
                                >
                                    {i + 1}
                                </div>
                            );
                        }

                        if (end < totalPages - 2) middlePages.push(<div key="dots-right" className="page dots">...</div>);

                        return middlePages;
                    })()}
                    {totalPages > 1 && (
                        <div className={page === totalPages - 1 ? "page active link" : "page link"}
                             onClick={handleNumberClick}
                             id={(totalPages - 1).toString()}
                        >
                            {totalPages}
                        </div>
                    )}
                </div>
                <div className="table-right button link" onClick={() => handleNextPageClick()}>{">"}</div>
            </div>

            {dialogText && dialogText ? <GenericDialog title={dialogTitle} text={dialogText} buttons={buttons}
                                                       result={handleErrorDialogResult.bind(this)}/> : null}
        </div>
    )


}

export default Accreditation